import {
  type Column,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type Row,
  type RowData,
  type Table,
  useReactTable,
} from "@tanstack/react-table";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { useImmer } from "use-immer";
import {
  type CSSProperties,
  Fragment,
  type MutableRefObject,
  type ReactElement,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import classes from "./TanStackTable.module.less";
import classNames from "classnames";
import { LinkButton, LinkType } from "~/common/components/Link";
import { Icon } from "~/common/components/icon/Icon";
import { IconType } from "~/common/components/icon/IconType";
import { getCssSize, getSize, getWidth } from "~/common/components/Table/TanStackTableUtils";
import { type TableOptions } from "@tanstack/table-core";
import { IconSize } from "~/common/components/icon/IconSize";
import { PopperTooltip } from "~/common/components/Tooltip";

import { isNonNull } from "~/common/utils/reducers";
import { TableScrollController } from "~/common/components/Table/TableScrollController";
import { useIsStuck } from "~/common/hooks/useIsStuck";
import {
  makeInitialWindowOffset,
  makeObserveWindowOffset,
  makeWindowScrollOffset,
} from "~/common/utils/TanstackVirtualUtils";

// Only solid for now
type PinnedVariant = "solid"; // | "shadow";

const VARIANTS = {
  lines: classes.tableLines,
  stripes: classes.tableStripes,
  clear: classes.tableClear,
  product: classes.tableStripes + " " + classes.tableProducts,
  hsaDetails: classes.tableDetailHSA,
  productComparison: classes.tableStripes + " " + classes.tableProductComparison,
  "": "",
};

interface VirtualizationOptions<TData> {
  estimateRowSize: (index: number, row: Row<TData> | undefined) => number;
  overscan?: number;
  scrollPaddingStart?: number;
  scrollPaddingEnd?: number;
}

interface Props<TData> extends Omit<TableOptions<TData>, "getCoreRowModel"> {
  className?: string;

  data: TData[];

  rootRef?: MutableRefObject<HTMLDivElement | null>;
  columns: TableOptions<TData>["columns"];
  pinnedVariant?: PinnedVariant;

  tableTitle?: ReactNode;
  renderDetails?: (props: { row: Row<TData> }) => ReactElement;
  variant?: "lines" | "stripes" | "clear" | "product" | "hsaDetails" | "productComparison" | "";
  fullwidth?: boolean;

  stickyHeader?: boolean;
  scrollbarOnTop?: boolean;
  virtualized?: VirtualizationOptions<TData>;
}

export function TanStackTable<TData>({
  data,
  columns,
  className,
  rootRef,
  renderDetails,
  tableTitle,
  variant = "lines",
  fullwidth,
  stickyHeader,
  scrollbarOnTop,
  pinnedVariant = "solid",
  virtualized,
  ...rest
}: Props<TData>) {
  const isSortingEnabled = columns.some((def) => def.enableSorting);
  const table = useReactTable<TData>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isSortingEnabled ? getSortedRowModel() : undefined,
    ...rest,
  });

  const hasHeader = !tableTitle && table.getHeaderGroups().length > 0;
  const allColumnsHaveSize = columns.every((column) => column.size);

  let stickyHeaderVariant: undefined | "simple" | "split" = undefined;
  if (stickyHeader) {
    const hasAnyPinnedColumns = table.getLeafHeaders().some((header) => header.column.getIsPinned());
    if (!hasAnyPinnedColumns) {
      stickyHeaderVariant = "simple";
    } else {
      stickyHeaderVariant = "split";

      // Has pinned columns. Do all have a set width?
      if (!allColumnsHaveSize) {
        console.error("Not implemented: sticky header with pinned columns and no size set.");
      }
    }
  }

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement | null>(null);
  const isVirtualized = virtualized !== undefined && allColumnsHaveSize;

  if (virtualized && !allColumnsHaveSize) {
    console.error("Not implemented: virtualized TanStackTable without explicit column sizes.");
  }

  const enableStuckCornerFix = !!stickyHeader;
  const { ref: stuckRef, isStuck } = useIsStuck<HTMLElement>(enableStuckCornerFix);

  const [isScrolled, setIsScrolled] = useState(false);
  const [isScrolledRight, setIsScrolledRight] = useState(false);
  useEffect(() => {
    if (tableContainerRef.current) {
      const element = tableContainerRef.current;
      const fn = () => {
        const el = tableContainerRef.current;
        if (!el) {
          return;
        }
        setIsScrolled(el.scrollLeft > 0);
        setIsScrolledRight(Math.ceil(el.scrollLeft) + el.clientWidth < el.scrollWidth);
      };
      fn();
      element?.addEventListener("scroll", fn, { passive: true });
      return () => element?.removeEventListener("scroll", fn);
    }
  });

  // Scroll slider state - only track dimensions for showing/hiding slider
  const [scrollDimensions, setScrollDimensions] = useImmer({
    scrollWidth: 0,
    clientWidth: 0,
  });

  // Calculate pinned left width once when columns change
  const pinnedLeftWidth = useMemo(() => {
    const pinnedLeftHeaders = table.getLeftLeafHeaders();
    let totalWidth = 0;
    pinnedLeftHeaders.forEach((header) => {
      const size = getSize(header.column);
      if (size && size.unit === "px") {
        totalWidth += size.value;
      }
    });
    return totalWidth;
  }, [columns]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate if there are scrollable columns (columns beyond pinned left)
  const hasScrollableColumns = table.getCenterLeafHeaders().length > 0 || table.getRightLeafHeaders().length > 0;
  const hasOverflow = scrollDimensions.scrollWidth > scrollDimensions.clientWidth;
  const showScrollSlider = scrollbarOnTop && hasScrollableColumns && hasOverflow;

  // Track dimensions with ResizeObserver - only for determining if slider should show
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (!tableContainer) {
      return;
    }

    let rafId: number;

    const updateDimensions = () => {
      setScrollDimensions((draft) => {
        if (tableContainerRef.current) {
          draft.scrollWidth = tableContainerRef.current.scrollWidth;
          draft.clientWidth = tableContainerRef.current.clientWidth;
        }
      });
    };

    const handleUpdate = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateDimensions);
    };

    const observer = new ResizeObserver(handleUpdate);
    observer.observe(tableContainer);

    window.addEventListener("resize", handleUpdate, { passive: true });

    updateDimensions();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", handleUpdate);
      cancelAnimationFrame(rafId);
    };
  }, [setScrollDimensions]);

  function renderTableContainer(children: ReactNode) {
    return (
      <div
        className={classNames(
          variant !== "productComparison" && stickyHeaderVariant !== "simple" && classes.tableContainer,
        )}
        ref={tableContainerRef}
      >
        {children}
      </div>
    );
  }
  function renderStickyTableContainer(children: ReactNode) {
    return (
      <div
        className={classNames(
          variant !== "productComparison" && stickyHeaderVariant !== "simple" && classes.tableContainer,
          classes.sticky,
          stickyHeaderVariant === "split" && classes.hideScrollbar,
          enableStuckCornerFix && classes.stickyCornerSentinel,
        )}
        ref={
          enableStuckCornerFix
            ? (node: HTMLDivElement | null) => {
                stuckRef(node);
                stickyHeaderRef.current = node;
              }
            : stickyHeaderRef
        }
      >
        {children}
      </div>
    );
  }

  function renderTable(...elements: ReactNode[]) {
    const tableRef =
      enableStuckCornerFix && stickyHeaderVariant === "simple"
        ? (node: HTMLTableElement | null) => stuckRef(node)
        : undefined;

    return (
      <table
        className={classNames(
          classes.table,
          VARIANTS[variant],
          fullwidth && classes.fullWidth,
          stickyHeaderVariant === "simple" && classes.sticky,
          enableStuckCornerFix && stickyHeaderVariant === "simple" && classes.stickyCornerSentinel,
          allColumnsHaveSize && classes.fixedLayout,
        )}
        ref={tableRef}
      >
        {elements}
      </table>
    );
  }

  function renderTableHeader() {
    if (!hasHeader) {
      return null;
    }

    return (
      <thead key={"head"} className={classNames(classes.header, stickyHeaderVariant === "simple" && classes.sticky)}>
        <tr>{renderHeaderCells(table, isScrolled, isScrolledRight)}</tr>
      </thead>
    );
  }

  function renderTableBody() {
    const rows = table.getRowModel().rows;

    if (isVirtualized && virtualized) {
      return (
        <VirtualizedTableBody
          table={table}
          virtualized={virtualized}
          isScrolled={isScrolled}
          isScrolledRight={isScrolledRight}
          renderDetails={renderDetails}
        />
      );
    }

    return (
      <tbody key={"body"}>
        {rows.map((row, idx) => (
          <Fragment key={row.id}>
            <tr className={classNames(classes.bodyRow, idx % 2 ? classes.evenRow : classes.oddRow)}>
              {renderBodyCells(row, isScrolled, isScrolledRight)}
            </tr>
            {row.getIsExpanded() && (
              <tr className={classNames(classes.bodyRow, idx % 2 ? classes.evenRow : classes.oddRow)}>
                <td colSpan={row.getVisibleCells().length}>{renderDetails?.({ row })}</td>
              </tr>
            )}
          </Fragment>
        ))}
      </tbody>
    );
  }

  return (
    <div
      className={classNames(
        className,
        variant === "productComparison" && stickyHeaderVariant !== "simple" && classes.tableContainer,
        `pinned-variant-${pinnedVariant}`,
        isScrolled && "is-scrolled-left",
        isScrolledRight && "is-scrolled-right",
        enableStuckCornerFix && isStuck && "is-stuck",
      )}
      ref={rootRef}
      data-stuck={enableStuckCornerFix && isStuck ? "true" : undefined}
      style={{
        position: "relative",
        ...(enableStuckCornerFix && isStuck && { borderRadius: 0 }),
      }}
    >
      {tableTitle && <div className={classes.title}>{tableTitle}</div>}
      {showScrollSlider && (
        <TableScrollController
          classes={{ root: "scroll-slider__root" }}
          tableContainerRef={tableContainerRef}
          stickyHeaderRef={stickyHeaderRef}
          pinnedLeftWidth={pinnedLeftWidth}
          stickyHeader={stickyHeader}
        />
      )}
      {stickyHeaderVariant !== "split" && renderTableContainer(renderTable(renderTableHeader(), renderTableBody()))}
      {stickyHeaderVariant === "split" && (
        <>
          {renderStickyTableContainer(renderTable(renderTableHeader()))}
          {/*{renderStickyTableContainer(renderTable(renderTableHeader(), renderTableBody({ visible: false })))}*/}
          {renderTableContainer(renderTable(renderTableBody()))}
        </>
      )}
    </div>
  );
}

function renderSpacerRow<TData>(row: Row<TData> | undefined, height: number, key: string) {
  if (!row) {
    return null;
  }

  return (
    <tr key={key} aria-hidden="true">
      {row.getVisibleCells().map((cell) => (
        <td
          key={cell.id}
          style={{
            width: getWidthFromColumn(cell.column),
            height,
            padding: 0,
            border: "none",
            background: "transparent",
          }}
        />
      ))}
    </tr>
  );
}

interface VirtualizedTableBodyProps<TData> {
  table: Table<TData>;
  virtualized: VirtualizationOptions<TData>;
  isScrolled: boolean;
  isScrolledRight: boolean;
  renderDetails?: (props: { row: Row<TData> }) => ReactElement;
}

function VirtualizedTableBody<TData>({
  table,
  virtualized,
  isScrolled,
  isScrolledRight,
  renderDetails,
}: VirtualizedTableBodyProps<TData>) {
  const rows = table.getRowModel().rows;
  const virtualBodyRef = useRef<HTMLTableSectionElement>(null);
  const initialOffset = useMemo(() => makeInitialWindowOffset(virtualBodyRef), []);
  const observeElementOffset = useMemo(() => makeObserveWindowOffset(virtualBodyRef), []);
  const scrollToFn = useMemo(() => makeWindowScrollOffset(virtualBodyRef), []);

  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    getItemKey: (index) => rows[index]?.id ?? String(index),
    estimateSize: (index) => virtualized.estimateRowSize(index, rows[index]),
    overscan: virtualized.overscan ?? 8,
    initialOffset,
    scrollPaddingEnd: virtualized.scrollPaddingEnd ?? 200,
    scrollPaddingStart: virtualized.scrollPaddingStart ?? 200,
    // @ts-expect-error TanStack virtualizer expects a narrower callback signature here.
    observeElementOffset,
    scrollToFn,
  });

  const expandedState = table.getState().expanded;
  useEffect(() => {
    rowVirtualizer.measure();
  }, [expandedState, rowVirtualizer]);

  const virtualItems = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();
  const firstVisibleStart = virtualItems[0]?.start ?? 0;
  const lastVisibleEnd = virtualItems[virtualItems.length - 1]?.end ?? 0;
  const bottomSpacerHeight = totalSize - lastVisibleEnd;

  return (
    <Fragment key={"body"}>
      <tbody ref={virtualBodyRef}>
        {firstVisibleStart > 0 && renderSpacerRow(rows[0], firstVisibleStart, "top-spacer")}
      </tbody>
      {virtualItems.map((virtualRow) => {
        const row = rows[virtualRow.index];
        if (!row) {
          return null;
        }

        const idx = virtualRow.index;
        return (
          <tbody key={row.id} data-index={virtualRow.index} ref={(node) => rowVirtualizer.measureElement(node)}>
            <tr className={classNames(classes.bodyRow, idx % 2 ? classes.evenRow : classes.oddRow)}>
              {renderBodyCells(row, isScrolled, isScrolledRight)}
            </tr>
            {row.getIsExpanded() && (
              <tr className={classNames(classes.bodyRow, idx % 2 ? classes.evenRow : classes.oddRow)}>
                <td colSpan={row.getVisibleCells().length}>{renderDetails?.({ row })}</td>
              </tr>
            )}
          </tbody>
        );
      })}
      <tbody>{bottomSpacerHeight > 0 && renderSpacerRow(rows[0], bottomSpacerHeight, "bottom-spacer")}</tbody>
    </Fragment>
  );
}

const getWidthFromColumn = <T extends RowData>(column: Column<T>) => {
  const size = getSize(column);
  if (!size) {
    return undefined;
  }
  return getCssSize(size);
};

const CLASS_FACTORIES: ((
  isLeftPinned: boolean,
  isLastLeftLeaf: boolean,
  isFirstRightLeaf: boolean,
  isRightPinned: boolean,
) => (string | boolean)[])[] = [
  (isLeftPinned: boolean, isLastLeftLeaf: boolean) => [
    isLeftPinned && classes.pinnedLeft,
    isLastLeftLeaf && classes.pinnedLeftLast,
    isLastLeftLeaf && "pinned-left-last",
  ],
  () => [],
  (_1, _2, isFirstRightLeaf: boolean, isRightPinned: boolean) => [
    isRightPinned && classes.pinnedRight,
    isFirstRightLeaf && classes.pinnedRightFirst,
    isFirstRightLeaf && "pinned-right-first",
  ],
];

const CELL_TYPE_CLASSES = {
  data: classes.cellData,
  action: classes.cellAction,
};

function renderHeaderCells<TData>(table: Table<TData>, isScrolled: boolean, isScrolledRight: boolean) {
  const cells = [table.getLeftLeafHeaders(), table.getCenterLeafHeaders(), table.getRightLeafHeaders()] as const;
  return cells.map((cells, cGroupIdx) => (
    <Fragment key={cGroupIdx}>
      {cells.map((header, idx, list) => {
        const isLeftPinned = cGroupIdx === 0;
        const isLastLeftLeaf = isLeftPinned && list[list.length - 1] === header;
        const isRightPinned = cGroupIdx === 2;
        const isFirstRightLeaf = isRightPinned && list[0] === header;
        const column = header.column;
        const meta = column.columnDef?.meta ?? {};
        const type = meta.columnType ?? "data";
        const textAlign = meta.align ?? "left";
        const sideClasses = CLASS_FACTORIES[cGroupIdx](isLeftPinned, isLastLeftLeaf, isFirstRightLeaf, isRightPinned);

        const cls = classNames(classes.headerCell, sideClasses, CELL_TYPE_CLASSES[type], meta.className);

        const style: CSSProperties = {
          width: getWidthFromColumn(column),
          textAlign: textAlign,
        };
        if (column.getIsPinned() === "left") {
          const sizes = list
            .slice(0, list.indexOf(header))
            .map((c) => c.column)
            .map(getSize);
          style.left = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        } else if (column.getIsPinned() === "right") {
          const sizes = list
            .slice(list.indexOf(header) + 1)
            .map((c) => c.column)
            .map(getSize);
          style.right = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        }

        const canSort = header.column.columnDef.enableSorting === true;
        const isSorted = header.column.getIsSorted();

        let content;
        if (header.isPlaceholder) {
          content = null;
        } else {
          if (!canSort) {
            content = flexRender(header.column.columnDef.header, header.getContext());
          } else {
            content = (
              <LinkButton
                variant={isSorted ? LinkType.PRIMARY : LinkType.NAVIGATION}
                onClick={() => header.column.toggleSorting(isSorted === "asc")}
                className={classes.sortHeader}
              >
                <Icon
                  type={IconType.ArrowUp}
                  size={IconSize.FONT_10}
                  className={classNames(
                    classes.sortIcon,
                    isSorted === "desc" && classes.sortIconDesc,
                    isSorted === "asc" && classes.sortIconAsc,
                  )}
                />
                <div>{flexRender(header.column.columnDef.header, header.getContext())}</div>
              </LinkButton>
            );
          }
          if (meta.tooltip) {
            content = (
              <PopperTooltip tooltipContent={meta.tooltip} className={"d-i-flex"}>
                {content}
              </PopperTooltip>
            );
          }
        }

        return (
          <th key={header.id} colSpan={header.colSpan} className={cls} style={style}>
            {content}
          </th>
        );
      })}
    </Fragment>
  ));
}
function renderBodyCells<TData>(row: Row<TData>, isScrolled: boolean, isScrolledRight: boolean) {
  const cells = [row.getLeftVisibleCells(), row.getCenterVisibleCells(), row.getRightVisibleCells()] as const;

  return cells.map((cells, cGroupIdx) => (
    <Fragment key={cGroupIdx}>
      {cells.map((cell, idx, list) => {
        const isLeftPinned = cGroupIdx === 0;
        const isLastLeftLeaf = isLeftPinned && list[list.length - 1] === cell;
        const isRightPinned = cGroupIdx === 2;
        const isFirstRightLeaf = isRightPinned && list[0] === cell;
        const column = cell.column;
        const meta = column.columnDef?.meta ?? {};
        const type = meta.columnType ?? "data";
        const textAlign = meta.align ?? "left";
        const sideClasses = CLASS_FACTORIES[cGroupIdx](isLeftPinned, isLastLeftLeaf, isFirstRightLeaf, isRightPinned);

        const getBodyCellClasses = meta.getBodyCellClasses;

        const cls = classNames(
          classes.bodyCell,
          sideClasses,
          CELL_TYPE_CLASSES[type],
          meta.className,
          getBodyCellClasses?.(row),
        );

        const style: CSSProperties = {
          width: getWidthFromColumn(column),
          textAlign: textAlign,
        };
        if (column.getIsPinned() === "left") {
          const sizes = list
            .slice(0, list.indexOf(cell))
            .map((c) => c.column)
            .map(getSize);
          style.left = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        } else if (column.getIsPinned() === "right") {
          const sizes = list
            .slice(list.indexOf(cell) + 1)
            .map((c) => c.column)
            .map(getSize);
          style.right = sizes.length > 0 && sizes.every(isNonNull) ? getWidth(sizes) : 0;
        }

        return (
          <td className={cls} key={cell.id} style={style}>
            {flexRender(column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </Fragment>
  ));
}
